import { fetchAuthSession } from 'aws-amplify/auth';
import { SocialProviders } from './types';
import { getBFFData, Queries } from '../api';

async function getSession() {
  return fetchAuthSession();
}

export async function getJWT() {
  try {
    const session = await fetchAuthSession();
    if (session.tokens?.accessToken) {
      return session.tokens.accessToken.toString();
    }
    // No user-pool tokens (guest user) — nothing to return
    return null;
  } catch {
    // Cached session failed (e.g. access token expired) — try a forced refresh.
    // If the refresh token is also expired this will throw and we return null.
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      return session.tokens?.accessToken.toString() ?? null;
    } catch {
      return null;
    }
  }
}

export async function getIdPayload() {
  try {
    return (await getSession()).tokens?.idToken?.payload;
  } catch (error) {
    return null;
  }
}

export async function getSocialUserName({
  provider,
  credential,
}: {
  provider: SocialProviders;
  credential?: string;
}) {
  if (provider === SocialProviders.facebook) {
    if (!window.FB) {
      throw 'FB SDK not found';
    }

    const accessToken = await getFBToken();
    if (!accessToken) {
      throw 'Failed to get the token';
    }

    const response = await getBFFData(Queries.getFbUsername, {
      payload: { accessToken },
    });
    if (!response.ok) {
      throw 'Bad BFF response';
    }

    const { username } = response.data.socialFb;
    if (!username) {
      throw 'Failed to get the username';
    }

    return { username, accessToken };
  } else if (provider === SocialProviders.google) {
    if (!credential) {
      throw 'Failed to get the token';
    }

    const response = await getBFFData(Queries.getGoogleUsername, {
      payload: { credential },
    });
    if (!response.ok) {
      throw 'Bad BFF response';
    }

    const { username } = response.data.socialGoogle;
    if (!username) {
      throw 'Failed to get the username';
    }

    return { username, accessToken: credential };
  } else if (provider === SocialProviders.apple) {
    if (!window.AppleID) {
      throw 'Apple SDK not found';
    }

    const { idToken, email, firstName, lastName } = await getAppleToken();

    if (!idToken) {
      throw 'Failed to get the token';
    }

    const response = await getBFFData(Queries.getAppleUsername, {
      payload: {
        idToken,
        userEmail: email,
        userFirstName: firstName,
        userLastName: lastName,
      },
    });

    if (!response.ok) {
      throw 'Bad BFF response';
    }

    const { username } = response.data.socialApple;
    if (!username) {
      throw 'Failed to get the username';
    }

    return { username, accessToken: idToken };
  }
  throw 'Bad provider';
}

async function getFBToken(): Promise<string> {
  function getLoginStatus(): Promise<{ status: string; authResponse: { accessToken: string } }> {
    return new Promise((resolve) => {
      window.FB?.getLoginStatus((response) => {
        resolve(response);
      });
    });
  }

  function login(): Promise<{ status: string; authResponse: { accessToken: string } }> {
    return new Promise((resolve) => {
      window.FB?.login(
        (response) => {
          resolve(response);
        },
        { scope: 'public_profile,email' }
      );
    });
  }

  const existingLogin = await getLoginStatus();
  if (existingLogin.status === 'connected') {
    return existingLogin.authResponse.accessToken;
  }

  const response = await login();
  if (response.status === 'connected') {
    return response.authResponse.accessToken;
  }
  throw 'FB Authentication failed';
}

async function getAppleToken(): Promise<{
  email?: string;
  firstName?: string;
  lastName?: string;
  idToken: string;
}> {
  try {
    const response = await window.AppleID.auth.signIn();
    return {
      idToken: response.authorization.id_token,
      email: response.user?.email,
      firstName: response.user?.name.firstName,
      lastName: response.user?.name.lastName,
    };
  } catch (error) {
    throw 'Apple Authentication failed';
  }
}
